(() => {
  "use strict";

  const selectors = {
    sidebar: "[data-collection-sidebar]",
    touchLeft: "[data-range-left]",
    touchRight: "[data-range-right]",
    line: "[data-range-line]",
    holder: "[data-range-holder]",
    minInput: "[data-field-price-min]",
    maxInput: "[data-field-price-max]",
    minLabel: "[data-range-label-min]",
    maxLabel: "[data-range-label-max]",
  };

  const attrs = {
    min: "data-se-min",
    max: "data-se-max",
    minValue: "data-se-min-value",
    maxValue: "data-se-max-value",
    step: "data-se-step",
    shouldUpdateFilter: "data-range-filter-update",
  };

  const classes = {
    initialized: "is-initialized",
  };

  if (!customElements.get("range-slider")) {
    customElements.define(
      "range-slider",
      class extends HTMLElement {
        constructor() {
          super();

          this.sidebar = this.closest(selectors.sidebar);
          this.touchLeft = this.querySelector(selectors.touchLeft);
          this.touchRight = this.querySelector(selectors.touchRight);
          this.lineSpan = this.querySelector(selectors.line);

          this.min = parseFloat(this.getAttribute(attrs.min));
          this.max = parseFloat(this.getAttribute(attrs.max));
          this.step = 0;
          this.normalizeFact = 26;
          this.startX = 0;
          this.x = 0;
          this.selectedTouch = null;

          this.sidebarTransitionEvent = (event) => this.onSidebarTransitionEnd(event);
          this.resizeEvent = () => {
            this.connectedCallback();

            if (this.sidebar) {
              this.sidebar.addEventListener("transitionend", this.sidebarTransitionEvent);
            }
          };
          this.onMoveEvent = (event) => this.onMove(event);
          this.onStopEvent = () => this.onStop();
          this.onStartEvent = (event) => this.onStart(event);

          document.addEventListener("theme:resize:width", this.resizeEvent);
        }

        connectedCallback() {
          let minValue = this.min;
          if (this.hasAttribute(attrs.minValue)) {
            minValue = parseFloat(this.getAttribute(attrs.minValue));
          }

          let maxValue = this.max;
          if (this.hasAttribute(attrs.maxValue)) {
            maxValue = parseFloat(this.getAttribute(attrs.maxValue));
          }

          if (minValue < this.min) minValue = this.min;
          if (maxValue > this.max) maxValue = this.max;
          if (minValue > maxValue) minValue = maxValue;

          if (this.getAttribute(attrs.step)) {
            this.step = Math.abs(parseFloat(this.getAttribute(attrs.step)));
          }

          this.reset();
          this.maxX = this.offsetWidth - this.touchRight.offsetWidth;
          this.selectedTouch = null;
          this.initialValue = this.lineSpan.offsetWidth - this.normalizeFact;

          this.setMinValue(minValue);
          this.setMaxValue(maxValue);

          this.touchLeft.addEventListener("mousedown", this.onStartEvent);
          this.touchRight.addEventListener("mousedown", this.onStartEvent);
          this.touchLeft.addEventListener("touchstart", this.onStartEvent, { passive: true });
          this.touchRight.addEventListener("touchstart", this.onStartEvent, { passive: true });

          this.onChange(this.getAttribute(attrs.minValue), this.getAttribute(attrs.maxValue));
          this.classList.add(classes.initialized);
        }

        reset() {
          this.touchLeft.style.left = "0px";
          this.touchRight.style.left = `${this.offsetWidth - this.touchLeft.offsetWidth}px`;
          this.lineSpan.style.marginLeft = "0px";
          this.lineSpan.style.width = `${this.offsetWidth - this.touchLeft.offsetWidth}px`;
          this.startX = 0;
          this.x = 0;
        }

        setMinValue(minValue) {
          const range = this.max - this.min;
          const ratio = range === 0 ? 0 : (minValue - this.min) / range;

          this.touchLeft.style.left = `${Math.ceil(ratio * (this.offsetWidth - (this.touchLeft.offsetWidth + this.normalizeFact)))}px`;
          this.lineSpan.style.marginLeft = `${this.touchLeft.offsetLeft}px`;
          this.lineSpan.style.width = `${this.touchRight.offsetLeft - this.touchLeft.offsetLeft}px`;
          this.setAttribute(attrs.minValue, minValue);
        }

        setMaxValue(maxValue) {
          const range = this.max - this.min;
          const ratio = range === 0 ? 0 : (maxValue - this.min) / range;

          this.touchRight.style.left = `${Math.ceil(ratio * (this.offsetWidth - (this.touchLeft.offsetWidth + this.normalizeFact)) + this.normalizeFact)}px`;
          this.lineSpan.style.marginLeft = `${this.touchLeft.offsetLeft}px`;
          this.lineSpan.style.width = `${this.touchRight.offsetLeft - this.touchLeft.offsetLeft}px`;
          this.setAttribute(attrs.maxValue, maxValue);
        }

        onStart(event) {
          let currentEvent = event;
          if (event.touches) currentEvent = event.touches[0];

          if (event.currentTarget === this.touchLeft) {
            this.x = this.touchLeft.offsetLeft;
          } else if (event.currentTarget === this.touchRight) {
            this.x = this.touchRight.offsetLeft;
          }

          this.startX = currentEvent.pageX - this.x;
          this.selectedTouch = event.currentTarget;

          document.addEventListener("mousemove", this.onMoveEvent);
          document.addEventListener("mouseup", this.onStopEvent);
          document.addEventListener("touchmove", this.onMoveEvent, { passive: true });
          document.addEventListener("touchend", this.onStopEvent, { passive: true });
        }

        onMove(event) {
          let currentEvent = event;
          if (event.touches) currentEvent = event.touches[0];

          this.x = currentEvent.pageX - this.startX;

          if (this.selectedTouch === this.touchLeft) {
            const leftLimit = this.touchRight.offsetLeft - this.selectedTouch.offsetWidth + 10;
            if (this.x > leftLimit) this.x = leftLimit;
            if (this.x < 0) this.x = 0;
            this.selectedTouch.style.left = `${this.x}px`;
          } else if (this.selectedTouch === this.touchRight) {
            const rightLimit = this.touchLeft.offsetLeft + this.touchLeft.offsetWidth - 10;
            if (this.x < rightLimit) this.x = rightLimit;
            if (this.x > this.maxX) this.x = this.maxX;
            this.selectedTouch.style.left = `${this.x}px`;
          }

          this.lineSpan.style.marginLeft = `${this.touchLeft.offsetLeft}px`;
          this.lineSpan.style.width = `${this.touchRight.offsetLeft - this.touchLeft.offsetLeft}px`;

          this.calculateValue();

          if (this.getAttribute("on-change")) {
            // eslint-disable-next-line no-new-func
            new Function("min, max", this.getAttribute("on-change"))(this.getAttribute(attrs.minValue), this.getAttribute(attrs.maxValue));
          }

          this.onChange(this.getAttribute(attrs.minValue), this.getAttribute(attrs.maxValue));
        }

        onStop() {
          document.removeEventListener("mousemove", this.onMoveEvent);
          document.removeEventListener("mouseup", this.onStopEvent);
          document.removeEventListener("touchmove", this.onMoveEvent);
          document.removeEventListener("touchend", this.onStopEvent);

          this.selectedTouch = null;
          this.calculateValue();
          this.onChanged(this.getAttribute(attrs.minValue), this.getAttribute(attrs.maxValue));
        }

        formatValue(value) {
          const numericValue = parseFloat(value);
          if (Number.isNaN(numericValue)) return "";

          const cents = Math.round(numericValue * 100);
          const moneyFormat =
            (window.theme && window.theme.moneyWithoutCurrencyFormat) ||
            (window.theme && window.theme.moneyFormat) ||
            "${{amount}}";

          if (window.theme && typeof window.theme.formatMoney === "function") {
            return window.theme.formatMoney(cents, moneyFormat).replace(/([,.])00(?=\D|$)/, "");
          }

          return String(numericValue);
        }

        updateLabels(minValue, maxValue) {
          const holder = this.closest(selectors.holder);
          if (!holder) return;

          const minLabel = holder.querySelector(selectors.minLabel);
          const maxLabel = holder.querySelector(selectors.maxLabel);

          if (minLabel) minLabel.textContent = this.formatValue(minValue);
          if (maxLabel) maxLabel.textContent = this.formatValue(maxValue);
        }

        onChange(minValue, maxValue) {
          const holder = this.closest(selectors.holder);
          if (holder) {
            const minInput = holder.querySelector(selectors.minInput);
            const maxInput = holder.querySelector(selectors.maxInput);

            if (minInput && maxInput) {
              minInput.value = minValue;
              maxInput.value = maxValue;
            }
          }

          this.updateLabels(minValue, maxValue);
        }

        onChanged() {
          if (this.hasAttribute(attrs.shouldUpdateFilter)) {
            this.dispatchEvent(new CustomEvent("theme:range:update", { bubbles: true }));
          }
        }

        calculateValue() {
          const width = this.lineSpan.offsetWidth - this.normalizeFact;
          const ratio = this.initialValue === 0 ? 0 : width / this.initialValue;

          let minValue = this.initialValue === 0 ? this.min : this.lineSpan.offsetLeft / this.initialValue;
          let maxValue = minValue + ratio;

          minValue = minValue * (this.max - this.min) + this.min;
          maxValue = maxValue * (this.max - this.min) + this.min;

          if (this.step !== 0) {
            minValue = this.step * Math.floor(minValue / this.step);
            maxValue = this.step * Math.floor(maxValue / this.step);
          }

          if (this.selectedTouch === this.touchLeft) {
            this.setAttribute(attrs.minValue, minValue);
          }

          if (this.selectedTouch === this.touchRight) {
            this.setAttribute(attrs.maxValue, maxValue);
          }
        }

        onSidebarTransitionEnd(event) {
          if (event.target === this.sidebar && event.propertyName === "min-width") {
            this.sidebar.removeEventListener("transitionend", this.sidebarTransitionEvent);
            this.connectedCallback();
          }
        }

        disconnectedCallback() {
          if (this.sidebar) {
            this.sidebar.removeEventListener("transitionend", this.sidebarTransitionEvent);
          }

          if (this.resizeEvent) {
            document.removeEventListener("theme:resize:width", this.resizeEvent);
          }
        }
      }
    );
  }
})();
